import Text from "common/Typography/Text";
import { AFFILIATION_AGREEMENT_TERMS_LINK } from "@shared/common/constants";
import Modal from "common/Modal/Modal";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Checkbox from "common/Checkbox/Checkbox";
import Button, { buttonStyles } from "common/Buttons/Button";
import Flex from "common/Boxes/Flex";
import Link from "common/Typography/Link";
import { affiliationService } from "app/services/affiliation.service";
import { displayResultMessage, showIntercomArticle } from "utils/misc";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage";
import { useHistory } from "react-router-dom";
import { DASHBOARD_PATH } from "common/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

import { LanguageNameSpaces } from "@shared/types";

interface AffiliationAgreementModalProps {
  visible: boolean;
  closeModal: () => void;
}

const AffiliationAgreementModal = ({
  visible,
  closeModal,
}: AffiliationAgreementModalProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const history = useHistory();

  const [acceptAffiliationTerms, { isLoading }] =
    affiliationService.useAcceptAffiliationTermsMutation();

  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const [showCheckboxError, setShowCheckboxError] = useState(false);

  useEffect(() => {
    if (isAgreementChecked) setShowCheckboxError(false);
  }, [isAgreementChecked]);

  const onConfirmAffiliation = () => {
    if (!isAgreementChecked) {
      setShowCheckboxError(true);
    } else {
      displayResultMessage(acceptAffiliationTerms().unwrap()).then(closeModal);
    }
  };

  const footer = (
    <Flex $justifyContent="end">
      <Link onClick={() => showIntercomArticle(AFFILIATION_ARTICLE_ID)}>
        {t("agreement.learn_more")}
      </Link>
      <StyledButton
        type="primary"
        $disabled={!isAgreementChecked}
        style={{ marginLeft: 16 }}
        onClick={onConfirmAffiliation}
        loading={isLoading}
      >
        {t("agreement.confirm")}
      </StyledButton>
    </Flex>
  );

  return (
    <Modal
      title={t("agreement.title")}
      open={visible}
      footer={footer}
      onCancel={() => {
        closeModal();
        history.push(DASHBOARD_PATH);
      }}
    >
      <Text>{t("agreement.body")}</Text>
      <StyledCheckbox
        checked={isAgreementChecked}
        onChange={(e) => setIsAgreementChecked(e.target.checked)}
      >
        <Text>
          {t("agreement.checkbox_prefix")}
          <Link href={AFFILIATION_AGREEMENT_TERMS_LINK} target="blank">
            {t("agreement.checkbox_link")}
          </Link>
        </Text>
      </StyledCheckbox>
      {showCheckboxError && (
        <RequiredFieldMessage
          requirement={showCheckboxError}
          errorMessage={t("agreement.error_must_accept")}
        />
      )}
    </Modal>
  );
};

export default AffiliationAgreementModal;

const AFFILIATION_ARTICLE_ID = "6814831";

const StyledCheckbox = styled(Checkbox)`
  margin-top: 12px;
`;
const StyledButton = styled(Button)<{ $disabled: boolean }>`
  ${(p) =>
    p.$disabled &&
    `
      ${buttonStyles.antBtnDisabled};
  `}
`;
